import { useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FocusScope } from "@radix-ui/react-focus-scope";
import { Bell, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { LoginContext } from "../../loginContext";
import { useOrganization } from "../../contexts/OrganizationContext";
import { useBuilderIdentity } from "../workflowBuilder/useBuilderIdentity";
import { claimWorkflowReminders, dismissWorkflowReminder } from "./api";
import { reminderOccurrenceKey, reminderResultPath, type ReminderNotice } from "./models";
import { ReminderSession } from "./reminderSession";
import { reminderNoticeText, reminderToastStrings } from "./reminderToastStrings";
import styles from "./WorkflowReminders.module.css";

export function WorkflowReminders() {
    const { loggedIn, isLoading, userInfo } = useContext(LoginContext);
    const { loading } = useOrganization();
    const { resolveToken, scope, switching } = useBuilderIdentity();
    const [consumerId] = useState(() => crypto.randomUUID());
    if (!loggedIn || isLoading || loading || switching || !userInfo.email) return null;
    return <ReminderNotifications key={scope} consumerId={consumerId} resolveToken={resolveToken} />;
}

type Props = { consumerId: string; resolveToken: () => Promise<string | undefined> };

function ReminderNotifications({ consumerId, resolveToken }: Props) {
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const language = i18n.resolvedLanguage ?? i18n.language;
    const text = reminderToastStrings(language);
    const [notices, setNotices] = useState<ReminderNotice[]>([]);
    const [keyboardActive, setKeyboardActive] = useState(false);
    const sessionRef = useRef<ReminderSession | null>(null);
    const viewportRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        const session = new ReminderSession(
            {
                claim: async signal => claimWorkflowReminders(consumerId, await resolveToken(), signal),
                dismiss: async (reminder, signal) => dismissWorkflowReminder(reminder, consumerId, await resolveToken(), signal)
            },
            setNotices
        );
        sessionRef.current = session;
        const updateAvailability = () => {
            if (document.visibilityState === "visible" && navigator.onLine) session.resume();
            else session.pause();
        };
        document.addEventListener("visibilitychange", updateAvailability);
        window.addEventListener("online", updateAvailability);
        window.addEventListener("offline", updateAvailability);
        updateAvailability();
        return () => {
            session.dispose();
            sessionRef.current = null;
            document.removeEventListener("visibilitychange", updateAvailability);
            window.removeEventListener("online", updateAvailability);
            window.removeEventListener("offline", updateAvailability);
        };
    }, [consumerId, resolveToken]);

    useEffect(() => {
        if (notices.length === 0) setKeyboardActive(false);
    }, [notices.length]);

    useEffect(() => {
        const handleShortcut = (event: KeyboardEvent) => {
            if (event.key === "F8" && notices.length > 0) {
                event.preventDefault();
                event.stopPropagation();
                setKeyboardActive(true);
                if (keyboardActive) viewportRef.current?.querySelector<HTMLButtonElement>("button:not(:disabled)")?.focus();
            } else if (event.key === "Escape" && keyboardActive && event.target instanceof Element && viewportRef.current?.contains(event.target)) {
                event.preventDefault();
                event.stopPropagation();
                const key = event.target.closest<HTMLElement>("[data-reminder-key]")?.dataset.reminderKey;
                if (key) void sessionRef.current?.dismiss(key);
            }
        };
        window.addEventListener("keydown", handleShortcut, true);
        return () => window.removeEventListener("keydown", handleShortcut, true);
    }, [keyboardActive, notices.length]);

    // Keep the live region mounted before content arrives, including while a modal is open.
    const content = (
        <aside
            ref={viewportRef}
            className={styles.viewport}
            aria-label={`${text.reminders} (F8)`}
            aria-keyshortcuts="F8"
            aria-live="polite"
            aria-relevant="additions text"
            // A modal's document-level scroll lock must not cancel scrolling in this viewport.
            onWheel={event => event.stopPropagation()}
            onTouchMove={event => event.stopPropagation()}
            onBlurCapture={event => {
                if (keyboardActive && event.relatedTarget instanceof Node && !event.currentTarget.contains(event.relatedTarget)) setKeyboardActive(false);
            }}
        >
            {notices.map(({ reminder, dismissing, dismissFailed }) => {
                const noticeText = reminderNoticeText(reminder, language);
                const resultPath = reminderResultPath(reminder);
                return (
                <section
                    key={reminderOccurrenceKey(reminder)}
                    data-reminder-key={reminderOccurrenceKey(reminder)}
                    className={styles.notice}
                    role="status"
                    aria-atomic="true"
                    onPointerDown={event => event.stopPropagation()}
                >
                    <div className={styles.heading}>
                        <Bell size={18} className={styles.icon} aria-hidden="true" />
                        <strong className={styles.title}>{noticeText.title}</strong>
                        <button
                            type="button"
                            className={styles.dismiss}
                            disabled={dismissing}
                            aria-label={`${text.dismiss}: ${noticeText.title}`}
                            onClick={() => void sessionRef.current?.dismiss(reminderOccurrenceKey(reminder))}
                        >
                            <X size={18} aria-hidden="true" />
                        </button>
                    </div>
                    <p className={styles.message}>{noticeText.message}</p>
                    {resultPath && <button type="button" className={styles.openResult} onClick={() => {
                        navigate(resultPath);
                        void sessionRef.current?.dismiss(reminderOccurrenceKey(reminder));
                    }}>{reminder.result_status === "failed" ? text.openFailedSummary : reminder.result_kind === "shift_handover" ? text.openHandover : reminder.result_format === "docx" ? text.openReport : text.openSummary}</button>}
                    {dismissFailed && (
                        <div className={styles.error}>
                            <p>{text.dismissFailed}</p>
                            <button type="button" disabled={dismissing} onClick={() => void sessionRef.current?.dismiss(reminderOccurrenceKey(reminder))}>
                                {text.retry}
                            </button>
                        </div>
                    )}
                </section>
            ); })}
        </aside>
    );
    return createPortal(
        keyboardActive ? (
            <FocusScope
                asChild
                onMountAutoFocus={event => {
                    event.preventDefault();
                    viewportRef.current?.querySelector<HTMLButtonElement>("button:not(:disabled)")?.focus();
                }}
            >
                {content}
            </FocusScope>
        ) : (
            content
        ),
        document.body
    );
}
